import React from "react";
import { BookOpen, CodeXml, RotateCw } from "lucide-react";
import { useHistory } from "react-router-dom";

import { Banner } from "@/design-system/components/Banner/Banner";
import { Button } from "@/design-system/components/Button/Button";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { useWizards } from "@/wizards/framework/WizardProvider";
import { INSTALL_SDK_WIZARD_TYPE } from "@/wizards/installSdk/constants";
import { EventOptions } from "../eventOptions";

const EVENT_LIBRARY_PATH = "/events/library";

function InstallButton() {
  const { openWizard } = useWizards();
  return (
    <Button
      variant="outline"
      size="sm"
      leadingIcon={<CodeXml aria-hidden />}
      onClick={() => openWizard(INSTALL_SDK_WIZARD_TYPE)}
    >
      Install Freshpaint
    </Button>
  );
}

function EventLibraryButton() {
  const history = useHistory();
  const { minimizeWizard } = useWizards();
  return (
    <Button
      variant="outline"
      size="sm"
      leadingIcon={<BookOpen aria-hidden />}
      onClick={() => {
        history.push(EVENT_LIBRARY_PATH);
        minimizeWizard();
      }}
    >
      Define an event
    </Button>
  );
}

type UnavailableProps = {
  eventOptions: Exclude<EventOptions, { status: "ready" }>;
  receivingEvents: boolean | null;
  retry: () => void;
};

/** Stands in for the event picker until there's something to pick. */
export function EventOptionsUnavailable({
  eventOptions,
  receivingEvents,
  retry,
}: UnavailableProps) {
  if (eventOptions.status === "loading") {
    return (
      <div className="flex items-center gap-3">
        <LoadingSpinner className="size-5" />
        <Typography variant="body-sm" color="subtle">
          Loading your events…
        </Typography>
      </div>
    );
  }

  if (eventOptions.status === "error") {
    return (
      <div className="flex flex-col items-start gap-3">
        <Banner type="error">
          Couldn’t load your events. Your choices so far are kept.
        </Banner>
        <Button
          variant="outline"
          size="sm"
          leadingIcon={<RotateCw aria-hidden />}
          onClick={retry}
        >
          Try again
        </Button>
      </div>
    );
  }

  const showInstall = receivingEvents === false;
  return (
    <div className="border-borderSubtle flex flex-col gap-4 rounded-lg border p-6">
      <div className="flex flex-col gap-1">
        <Typography variant="heading-sm" as="h3">
          No events to choose from yet
        </Typography>
        <Typography variant="body-sm" color="subtle">
          {showInstall
            ? "Conversions come from the events Freshpaint collects on your site. Install Freshpaint, then define the event that marks a conversion, like a visit to your booking confirmation page."
            : "Define the event that marks a conversion, like a visit to your booking confirmation page, then come back to choose it."}
        </Typography>
      </div>
      <div className="flex flex-wrap gap-2">
        {showInstall && <InstallButton />}
        <EventLibraryButton />
      </div>
      <Typography variant="body-xs" color="subtle">
        This wizard keeps your place, so you can come back to it anytime.
      </Typography>
    </div>
  );
}

type HintProps = {
  receivingEvents: boolean | null;
  hasUnprocessed: boolean;
};

function StatusNote({ receivingEvents, hasUnprocessed }: HintProps) {
  if (receivingEvents === null) {
    return (
      <Banner type="info" kind="note">
        Traffic stats aren’t available right now, so Freshpaint can’t recommend
        events. You can still choose from your Event Library.
      </Banner>
    );
  }
  if (!receivingEvents) {
    return (
      <Banner type="info" kind="note">
        <div className="flex flex-col items-start gap-2">
          <span>
            Freshpaint hasn’t seen traffic for these events yet, so it can’t
            recommend any. You can still choose them under Other events. Stats
            update overnight once your site sends events.
          </span>
          <InstallButton />
        </div>
      </Banner>
    );
  }
  if (hasUnprocessed) {
    return (
      <Banner type="info" kind="note">
        Stats for newly defined events appear after Freshpaint’s nightly update.
        You can choose them now.
      </Banner>
    );
  }
  return null;
}

/** Shown under the picker so a missing conversion has a way forward. */
export function MissingEventHint(props: HintProps) {
  return (
    <>
      <StatusNote {...props} />
      <DefineEventPrompt />
    </>
  );
}

export function DefineEventPrompt() {
  return (
    <Typography variant="body-xs" color="subtle">
      Don’t see the event you need?{" "}
      <WizardLink
        to={EVENT_LIBRARY_PATH}
        className="text-textActionSelected cursor-pointer underline"
      >
        Define it in the Event Library
      </WizardLink>
      , then come back to choose it.
    </Typography>
  );
}
